import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/api/client"
import { SkeletonZeilen } from "@/lib/Skeleton"
import { formatDatum, formatEuro } from "@/lib/format"
import { fehlerNachricht, useToast } from "@/lib/toast"
import { useDocumentTitle } from "@/lib/useDocumentTitle"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import type { Energieart, Preis } from "@/api/types"

function PreisSheet({
  open,
  onOpenChange,
  energieartId,
  braviertGueltigVon,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  energieartId: number
  braviertGueltigVon: boolean
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState("")
  const [euroProEinheit, setEuroProEinheit] = useState("")
  const [gueltigVon, setGueltigVon] = useState("")
  const [gueltigBis, setGueltigBis] = useState("")
  const [feldFehler, setFeldFehler] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    setName("")
    setEuroProEinheit("")
    setGueltigVon("")
    setGueltigBis("")
    setFeldFehler({})
  }, [open])

  const anlegen = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/energiearten/${energieartId}/preise`, {
          name: name || undefined,
          euroProEinheit,
          gueltigVon: braviertGueltigVon ? gueltigVon : undefined,
          gueltigBis,
        })
      ).data,
    onSuccess: () => {
      setFeldFehler({})
      queryClient.invalidateQueries({ queryKey: ["preise", energieartId] })
      toast.success("Preis angelegt.")
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const nachricht = fehlerNachricht(err, "Preis konnte nicht angelegt werden.")
      setFeldFehler({ form: nachricht })
      toast.error(nachricht, { retry: () => anlegen.mutate() })
    },
  })

  function absenden(e: React.FormEvent) {
    e.preventDefault()
    const neueFehler: Record<string, string> = {}
    if (braviertGueltigVon && !gueltigVon) neueFehler.gueltigVon = "Pflichtfeld"
    if (!gueltigBis) neueFehler.gueltigBis = "Pflichtfeld"
    if (!euroProEinheit) neueFehler.euroProEinheit = "Pflichtfeld"
    setFeldFehler(neueFehler)
    if (Object.keys(neueFehler).length > 0) {
      const ersteFeldId = braviertGueltigVon && neueFehler.gueltigVon
        ? "gueltigVon"
        : neueFehler.gueltigBis
          ? "gueltigBis"
          : "euroProEinheit"
      document.getElementById(ersteFeldId)?.focus()
      return
    }
    anlegen.mutate()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="energie.preis" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Preis anlegen</SheetTitle>
          <SheetDescription>Neuer Preiszeitraum für diese Energieart.</SheetDescription>
        </SheetHeader>
        <form className="grid gap-4 py-4" onSubmit={absenden} noValidate>
          {braviertGueltigVon && (
            <div className="space-y-1.5">
              <label className="block text-sm text-slate-600" htmlFor="gueltigVon">
                Gültig von (erster Preis)
              </label>
              <input
                id="gueltigVon"
                type="date"
                value={gueltigVon}
                onChange={(e) => setGueltigVon(e.target.value)}
                aria-invalid={!!feldFehler.gueltigVon}
                aria-describedby={feldFehler.gueltigVon ? "gueltigVon-fehler" : undefined}
                className={`h-9 w-full rounded-md border bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 ${feldFehler.gueltigVon ? "border-red-500" : "border-slate-300 focus:border-slate-400"}`}
              />
              {feldFehler.gueltigVon && (
                <p id="gueltigVon-fehler" className="mt-0.5 text-xs text-red-600">
                  {feldFehler.gueltigVon}
                </p>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="gueltigBis">
              Gültig bis
            </label>
            <input
              id="gueltigBis"
              type="date"
              value={gueltigBis}
              onChange={(e) => setGueltigBis(e.target.value)}
              aria-invalid={!!feldFehler.gueltigBis}
              aria-describedby={feldFehler.gueltigBis ? "gueltigBis-fehler" : undefined}
              className={`h-9 w-full rounded-md border bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 ${feldFehler.gueltigBis ? "border-red-500" : "border-slate-300 focus:border-slate-400"}`}
            />
            {feldFehler.gueltigBis && (
              <p id="gueltigBis-fehler" className="mt-0.5 text-xs text-red-600">
                {feldFehler.gueltigBis}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="euroProEinheit">
              Euro pro Einheit
            </label>
            <input
              id="euroProEinheit"
              type="number"
              step="0.000001"
              value={euroProEinheit}
              onChange={(e) => setEuroProEinheit(e.target.value)}
              aria-invalid={!!feldFehler.euroProEinheit}
              aria-describedby={feldFehler.euroProEinheit ? "euroProEinheit-fehler" : undefined}
              className={`h-9 w-full rounded-md border bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 ${feldFehler.euroProEinheit ? "border-red-500" : "border-slate-300 focus:border-slate-400"}`}
            />
            {feldFehler.euroProEinheit && (
              <p id="euroProEinheit-fehler" className="mt-0.5 text-xs text-red-600">
                {feldFehler.euroProEinheit}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="preisName">
              Name (optional)
            </label>
            <input
              id="preisName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
            />
          </div>
          {!braviertGueltigVon && (
            <p className="text-xs text-slate-400">
              Gültig-von wird serverseitig auf den Folgetag des letzten Preiszeitraums gesetzt
              (lückenlose Historie, ARCHITEKTUR.md §3.2).
            </p>
          )}
          {feldFehler.form && <p className="text-sm text-red-600">{feldFehler.form}</p>}
          <SheetFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={anlegen.isPending}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              {anlegen.isPending ? "Speichert…" : "Speichern"}
            </button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

function PreisHistorie({ energieartId }: { energieartId: number }) {
  const [sheetOffen, setSheetOffen] = useState(false)

  const preiseQuery = useQuery({
    queryKey: ["preise", energieartId],
    queryFn: async () => (await api.get<Preis[]>(`/energiearten/${energieartId}/preise`)).data,
  })

  const braviertGueltigVon = (preiseQuery.data ?? []).length === 0

  return (
    <div className="mt-3 rounded-md border border-slate-100 bg-slate-50 p-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="py-2.5 font-semibold">Gültig von</th>
              <th className="py-2.5 font-semibold">Gültig bis</th>
              <th className="py-2.5 font-semibold">€/Einheit</th>
              <th className="py-2.5 font-semibold">Name</th>
            </tr>
          </thead>
          <tbody>
            {preiseQuery.isLoading && <SkeletonZeilen zeilen={2} spalten={4} />}
            {preiseQuery.data?.map((p) => (
              <tr key={p.id} className="border-t border-slate-200">
                <td className="py-2.5 text-slate-700">{formatDatum(p.gueltigVon)}</td>
                <td className="py-2.5 text-slate-700">{formatDatum(p.gueltigBis)}</td>
                <td className="py-2.5 text-slate-700">{formatEuro(p.euroProEinheit)}</td>
                <td className="py-2.5 text-slate-500">{p.name ?? "—"}</td>
              </tr>
            ))}
            {!preiseQuery.isLoading && preiseQuery.data?.length === 0 && (
              <tr>
                <td colSpan={4} className="py-3 text-center text-slate-400">
                  Noch kein Preis erfasst — mit „Preis anlegen" den ersten Preiszeitraum anlegen.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setSheetOffen(true)}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          Preis anlegen
        </button>
      </div>

      <PreisSheet
        open={sheetOffen}
        onOpenChange={setSheetOffen}
        energieartId={energieartId}
        braviertGueltigVon={braviertGueltigVon}
      />
    </div>
  )
}

function EnergieartSheet({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState("")
  const [einheit, setEinheit] = useState("")
  const [feldFehler, setFeldFehler] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    setName("")
    setEinheit("")
    setFeldFehler({})
  }, [open])

  const anlegen = useMutation({
    mutationFn: async () =>
      (await api.post("/energiearten", { name, einheit: einheit || undefined })).data,
    onSuccess: () => {
      setFeldFehler({})
      queryClient.invalidateQueries({ queryKey: ["energiearten"] })
      toast.success("Energieart angelegt.")
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const nachricht = fehlerNachricht(err, "Energieart konnte nicht angelegt werden.")
      setFeldFehler({ form: nachricht })
      toast.error(nachricht, { retry: () => anlegen.mutate() })
    },
  })

  function absenden(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setFeldFehler({ name: "Pflichtfeld" })
      document.getElementById("energieart-name")?.focus()
      return
    }
    setFeldFehler({})
    anlegen.mutate()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="energie.energieart" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Neue Energieart</SheetTitle>
          <SheetDescription>Lückenlose Preishistorie (F3)</SheetDescription>
        </SheetHeader>
        <form className="grid gap-4 py-4" onSubmit={absenden} noValidate>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="energieart-name">
              Name
            </label>
            <input
              id="energieart-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={!!feldFehler.name}
              aria-describedby={feldFehler.name ? "energieart-name-fehler" : undefined}
              className={`h-9 w-full rounded-md border bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 ${feldFehler.name ? "border-red-500" : "border-slate-300 focus:border-slate-400"}`}
            />
            {feldFehler.name && (
              <p id="energieart-name-fehler" className="mt-0.5 text-xs text-red-600">
                {feldFehler.name}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="energieart-einheit">
              Einheit
            </label>
            <input
              id="energieart-einheit"
              type="text"
              placeholder="kWh, m³, …"
              value={einheit}
              onChange={(e) => setEinheit(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
            />
          </div>
          {feldFehler.form && <p className="text-sm text-red-600">{feldFehler.form}</p>}
          <SheetFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={anlegen.isPending}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              {anlegen.isPending ? "Speichert…" : "Speichern"}
            </button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export function EnergieartenPage() {
  useDocumentTitle("Energiearten & Preise")
  const [aufgeklappt, setAufgeklappt] = useState<number | null>(null)
  const [sheetOffen, setSheetOffen] = useState(false)

  const energieartenQuery = useQuery({
    queryKey: ["energiearten"],
    queryFn: async () => (await api.get<Energieart[]>("/energiearten")).data,
  })

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Energiearten & Preise</h1>
          <p className="mt-1 text-sm text-slate-500">Lückenlose Preishistorie (F3)</p>
        </div>
        <button
          type="button"
          onClick={() => setSheetOffen(true)}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          Energieart anlegen
        </button>
      </div>

      <EnergieartSheet open={sheetOffen} onOpenChange={setSheetOffen} />

      {energieartenQuery.isLoading && (
        <div className="mt-6 space-y-3">
          <div className="h-14 animate-pulse rounded-lg border border-slate-200 bg-white" />
          <div className="h-14 animate-pulse rounded-lg border border-slate-200 bg-white" />
        </div>
      )}

      {energieartenQuery.isError && (
        <div className="mt-6 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Energiearten konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => energieartenQuery.refetch()}
            className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {energieartenQuery.data?.map((art) => (
          <div key={art.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <button
              type="button"
              className="flex min-h-10 w-full items-center justify-between text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              aria-expanded={aufgeklappt === art.id}
              onClick={() => setAufgeklappt(aufgeklappt === art.id ? null : art.id)}
            >
              <span className="font-medium text-slate-900">
                {art.name} {art.einheit && <span className="text-slate-400">({art.einheit})</span>}
              </span>
              <span className="text-slate-400" aria-hidden="true">
                {aufgeklappt === art.id ? "▲" : "▼"}
              </span>
            </button>
            {aufgeklappt === art.id && <PreisHistorie energieartId={art.id} />}
          </div>
        ))}
        {!energieartenQuery.isLoading && energieartenQuery.data?.length === 0 && (
          <p className="text-slate-400">
            Noch keine Energiearten angelegt — mit „Energieart anlegen" die erste Energieart
            (z. B. „Strom", „Erdgas") anlegen.
          </p>
        )}
      </div>
    </div>
  )
}
